import { useEffect } from 'react';

import { Box, Heading, Flex, Text, Button, Link as ChakraLink } from '@chakra-ui/react';

import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { ChevronRightIcon } from '@chakra-ui/icons';
import Link from 'next/link';
import { configFile } from '@/config';
import { handleGTMClick } from '@/helpers/handleGTMClick';
import Breadcrumbs from './Breadcrumbs';

interface Props {
  title: string;
  description?: string;
  counter?: number | undefined;
  breadcrumbs?: any;
  button?: boolean;
  cenary?: boolean;
}

interface PathButtonIds {
  [key: string]: string;
}

const ITEM_PATH_TO_ID: PathButtonIds = {
  '/cenarios/seo-para-startups': 'scenario_startups',
  '/cenarios/seo-para-lojas-virtuais': 'scenario_ecommerce',
  '/cenarios/migracao-de-sites': 'scenario_migration',
  '/cenarios/auditoria-de-seo': 'scenario_audit',
  '/cenarios/seo-para-blogs': 'scenario_blogs',
  '/servicos/consultoria-em-seo': 'scenario_seo',
  '/servicos/local-seo': 'scenario_local_seo',
  '/servicos/conteudo': 'scenario_content',
  '/servicos/digital-analytics': 'scenario_analytics',
  '/politica-de-privacidade': 'scenario_privacy',
};

export default function BannerCategoryTag({ title, counter, description, breadcrumbs, button, cenary }: Props) {
  const { t } = useTranslation('common');
  const router = useRouter();

  useEffect(() => {
    const bannerCard = document.querySelectorAll('.onBanner');

    bannerCard[0]?.nextElementSibling?.classList.add('show');
    bannerCard.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        document.querySelector('.show')!.classList.remove('show');
        item.nextElementSibling!.classList.add('show');
      });
    });
  }, [title]);

  return (
    <Box
      as="section"
      width="100%"
      bgColor={configFile.primaryAccentColor}
      height={cenary ? { base: '840', md: '582' } : { base: '550', md: '582' }}
      paddingBottom={{ base: '0', lg: '80px' }}
      paddingTop={{ base: '0', lg: '80px' }}
      position="relative"
    >
      <Image
        fill
        priority
        style={{ objectFit: 'cover' }}
        src="/images/default/background.png"
        sizes="(max-width: 769px) 90vw, 1440px"
        alt={title}
      />
      <Box
        maxWidth="1216px"
        margin="0 auto"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        columnGap="32px"
        width="90%"
      >
        <Box display={{ md: 'flex' }} flexDirection="column" rowGap="16px" w="100%" height="100%">
          <Flex
            gap="16px"
            position="absolute"
            w={{ base: '90%', lg: '100%' }}
            top={cenary ? { base: '25%', lg: '50%' } : { base: '40%', lg: '50%' }}
            transform="translateY(-50%)"
            direction="column"
            pb={{ base: '40px' }}
          >
            <Breadcrumbs data={breadcrumbs} />
            <Flex
              alignItems={{ base: 'flex-start', lg: 'center' }}
              gap="24px"
              direction={{ base: 'column', lg: 'row' }}
            >
              <Heading
                color={configFile.tertiaryAccentColor}
                fontSize={{ base: '36px', lg: '60px' }}
                lineHeight={{ base: '40px', lg: '60px' }}
                as="h1"
              >
                {title}
              </Heading>

              {counter && (
                <Text
                  size="md"
                  as="span"
                  h="44px"
                  bgColor="gray.100"
                  color="gray.700"
                  display="flex"
                  fontWeight="400"
                  gap="10px"
                  lineHeight="24px"
                  justifyContent="center"
                  alignItems="center"
                  padding="10px"
                  borderRadius="4px"
                  mt="2px"
                >
                  {counter === 1 ? `${counter} publicação` : `${counter} publicações`}
                </Text>
              )}
            </Flex>
            {description && (
              <Text fontSize="18px" color={configFile.tertiaryAccentColor} maxWidth={{ base: 'auto', lg: '645px' }}>
                {description}
              </Text>
            )}
            {button && (
              <Button
                id="scenario_top_contact"
                onClick={(e) => handleGTMClick(e, () => router.push('/contato'))}
                aria-label="contact page"
                size="md"
                h="48px"
                maxW={{ base: '100%', lg: '289px' }}
                color={configFile.tertiaryAccentColor}
                bgColor={configFile.fourthAccentColor}
                _hover={{ bgColor: configFile.fiveAccentColor }}
                mt="32px"
              >
                {t('homepage.banner.button')}
              </Button>
            )}
            {cenary && (
              <Flex width={{ base: '100%', lg: '1216px' }} margin="0 auto" position="absolute" top="0">
                <CenaryComponent />
              </Flex>
            )}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}

function CenaryComponent() {
  const { route } = useRouter();

  return (
    <Flex
      direction="column"
      gap="24px"
      width={{ base: '100%', lg: '300px' }}
      height="350px"
      padding="32px"
      borderRadius="16px"
      bgColor={configFile.tertiaryAccentColor}
      position="absolute"
      right={{ lg: '0' }}
      top={{ lg: '250px' }}
      bottom={{ base: '-790px' }}
      transform={{ lg: 'translateY(-50%)' }}
      zIndex={3}
    >
      <Heading fontSize="16px" lineHeight="24px" fontWeight="700" color="teal.500">
        Outros cenários
      </Heading>

      {route !== '/cenarios/auditoria-de-seo' && (
        <ChakraLink
          id={ITEM_PATH_TO_ID['/cenarios/auditoria-de-seo']}
          onClick={handleGTMClick}
          onAuxClick={handleGTMClick}
          as={Link}
          href="/cenarios/auditoria-de-seo"
        >
          <Flex gap="8px" alignItems="center">
            <ChevronRightIcon color="gray.500" />
            <Text color="gray.600">Auditoria de SEO</Text>
          </Flex>
        </ChakraLink>
      )}
      {route !== '/cenarios/migracao-de-sites' && (
        <ChakraLink
          id={ITEM_PATH_TO_ID['/cenarios/migracao-de-sites']}
          onClick={handleGTMClick}
          onAuxClick={handleGTMClick}
          as={Link}
          href="/cenarios/migracao-de-sites"
        >
          <Flex gap="8px" alignItems="center">
            <ChevronRightIcon color="gray.500" />
            <Text color="gray.600">Migração de Sites</Text>
          </Flex>
        </ChakraLink>
      )}
      {route !== '/cenarios/seo-para-startups' && (
        <ChakraLink
          id={ITEM_PATH_TO_ID['/cenarios/seo-para-startups']}
          onClick={handleGTMClick}
          onAuxClick={handleGTMClick}
          as={Link}
          href="/cenarios/seo-para-startups"
        >
          <Flex gap="8px" alignItems="center">
            <ChevronRightIcon color="gray.500" />
            <Text color="gray.600">SEO para Startups</Text>
          </Flex>
        </ChakraLink>
      )}
      {route !== '/cenarios/seo-para-lojas-virtuais' && (
        <ChakraLink
          id={ITEM_PATH_TO_ID['/cenarios/seo-para-lojas-virtuais']}
          onClick={handleGTMClick}
          onAuxClick={handleGTMClick}
          as={Link}
          href="/cenarios/seo-para-lojas-virtuais"
        >
          <Flex gap="8px" alignItems="center">
            <ChevronRightIcon color="gray.500" />
            <Text color="gray.600">SEO para Lojas Virtuais</Text>
          </Flex>
        </ChakraLink>
      )}
      {route !== '/cenarios/seo-para-blogs' && (
        <ChakraLink
          id={ITEM_PATH_TO_ID['/cenarios/seo-para-blogs']}
          onClick={handleGTMClick}
          onAuxClick={handleGTMClick}
          as={Link}
          href="/cenarios/seo-para-blogs"
        >
          <Flex gap="8px" alignItems="center">
            <ChevronRightIcon color="gray.500" />
            <Text color="gray.600">SEO para Blogs</Text>
          </Flex>
        </ChakraLink>
      )}
    </Flex>
  );
}
